import { WingTranslator } from 'src/translators'
import {
  BulkUpdateWingDetailFormEntity,
  WingDetailEntity,
  WriteWingDetailFormEntity,
} from 'src/types'
import { useBulkUpdateWingsMutation } from 'src/types/generated'

type BulkUpdateWingsInput = {
  projectId: string
  formEntity: BulkUpdateWingDetailFormEntity
  wings: WingDetailEntity[]
}

export const useWing = () => {
  const [bulkUpdateWingsMutation] = useBulkUpdateWingsMutation()

  const hasChanged = (formEntity: WriteWingDetailFormEntity, wing: WingDetailEntity): boolean => {
    return (
      formEntity.name !== wing.name ||
      formEntity.priority !== wing.priority ||
      formEntity.roomTypes.some((roomTypeFormEntity, index) => {
        const targetRoomType = wing.roomTypes.at(index)
        return (
          roomTypeFormEntity.name !== targetRoomType?.name ||
          roomTypeFormEntity.priority !== targetRoomType?.priority ||
          !!roomTypeFormEntity.isDeleted
        )
      }) ||
      !!formEntity.isDeleted
    )
  }

  const extractModifiedWings = (
    formEntity: BulkUpdateWingDetailFormEntity,
    wings: WingDetailEntity[]
  ): BulkUpdateWingDetailFormEntity => {
    const modifiedWings = formEntity.items.filter(item => {
      const targetWing = wings.find(wing => wing.id === item.entityId)
      if (!targetWing) {
        return true
      }
      return hasChanged(item, targetWing)
    })

    return { items: modifiedWings }
  }

  const hasDiff = (
    formEntity: BulkUpdateWingDetailFormEntity,
    wings: WingDetailEntity[]
  ): boolean => {
    const modifiedWings = extractModifiedWings(formEntity, wings)
    return modifiedWings.items.length > 0
  }

  const bulkUpdateWings = async (input: BulkUpdateWingsInput) => {
    const { projectId, formEntity, wings } = input

    const changedFormEntity = extractModifiedWings(formEntity, wings)

    const params = WingTranslator.toRequest(changedFormEntity)
    await bulkUpdateWingsMutation({ variables: { projectId, params } })
  }

  return {
    bulkUpdateWings,
    extractModifiedWings,
    hasDiff,
  }
}
